import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";
import { EmailVerificationDialog } from "@/components/EmailVerificationDialog";
import { AuthFormFields } from "@/components/auth/AuthFormFields";
import { AuthSuggestions } from "@/components/auth/AuthSuggestions";
import { TurnstileWidget } from "@/components/auth/TurnstileWidget";
import { useAuthForm } from "@/components/auth/useAuthForm";

const Auth = () => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const {
    isLogin,
    setIsLogin,
    email,
    setEmail,
    password,
    setPassword,
    displayName,
    setDisplayName,
    loading,
    errors,
    setTurnstileToken,
    turnstileWidgetId,
    setTurnstileWidgetId,
    suggestions,
    loadingSuggestions,
    handleGetSuggestions,
    selectSuggestion,
    showVerificationDialog,
    setShowVerificationDialog,
    unverifiedEmail,
    currentAvatarUrl,
    generatingAvatar,
    handleTurnstileError,
    handleAuthSubmit,
    handleMagicLink,
  } = useAuthForm({ withAvatarPreview: true, signUpRedirectUrl: `${window.location.origin}/` });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    // Check if this is an email verification callback
    const isEmailVerification = hashParams.get("access_token") || hashParams.get("type") === "recovery";

    if (urlParams.get("mode") === "signup") {
      setIsLogin(false);
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);

      // Handle email verification
      if (event === "SIGNED_IN" && isEmailVerification) {
        toast.success("Email verified successfully!");

        if (!session?.user) {
          // Not logged in, show login form
          setIsLogin(true);
          toast.info("Please log in to continue");
        }
        // Don't navigate away - stay on current page
        return;
      }

      // Only navigate to home for manual login (not email verification)
      if (session?.user && !isEmailVerification) {
        navigate("/");
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      // Don't auto-navigate on page load if this is an email verification
      if (session?.user && !isEmailVerification) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="pb-2 pt-4">
          <CardTitle>{isLogin ? "Sign In" : "Sign Up"}</CardTitle>
          {isLogin && <CardDescription>Welcome back!</CardDescription>}
        </CardHeader>
        <CardContent className="pt-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleAuthSubmit();
            }}
            className="space-y-3"
          >
            <AuthFormFields
              isLogin={isLogin}
              email={email}
              password={password}
              displayName={displayName}
              errors={errors}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onDisplayNameChange={setDisplayName}
              onGetSuggestions={handleGetSuggestions}
              loadingSuggestions={loadingSuggestions}
              avatarUrl={currentAvatarUrl}
              generatingAvatar={generatingAvatar}
              showAvatarPreview
            />
            {!isLogin && <AuthSuggestions suggestions={suggestions} onSelect={selectSuggestion} />}
            {!isLogin && (
              <TurnstileWidget
                isActive={!isLogin}
                widgetId={turnstileWidgetId}
                setWidgetId={setTurnstileWidgetId}
                onTokenChange={setTurnstileToken}
                onError={handleTurnstileError}
              />
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Loading..." : isLogin ? "Sign In" : "Sign Up"}
            </Button>
          </form>

          {isLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleMagicLink}
                disabled={loading || !email}
              >
                Send Magic Link
              </Button>
            </>
          )}

          <div className="mt-2 text-center">
            <Button variant="ghost" onClick={() => setIsLogin(!isLogin)} className="text-sm h-8">
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <EmailVerificationDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        email={unverifiedEmail}
      />
    </div>
  );
};

export default Auth;
